import React from 'react';
import classNames from 'classnames';
import styled from '@emotion/styled';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import Tooltip, { TooltipPrimitive } from '@atlaskit/tooltip';
import EmojiFrequentIcon from '@atlaskit/icon/glyph/emoji/frequent';
import { getDateAndHourDifferenceToNow } from '@css-xp/ppc-utils';
import { NoData } from '@css-xp/ppc-common';

type DateType = 'expiry-date' | 'updated-date' | 'created-date';
interface Props {
  date: string | number | null;
  dateType?: DateType;
}

const QuoteInlineDialog = styled(TooltipPrimitive)`
  background: white;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  box-sizing: content-box; /* setting this to content-box or else it will break the overflow handling */
  padding: 8px;
  max-width: 130px;
  .tooltip-content-quote-date-and-time {
    flex-direction: column;
    .tooltip-quote-time {
      font-size: 12px;
      color: grey;
    }
  }
`;

export const QuoteDateAndTimeDuration = ({ date, dateType }: Props) => {
  if (!date) return <NoData />;

  const { result, isLessThanFiveDays, hasExpired } = getDateAndHourDifferenceToNow(date, dateType);
  const utcDate = utcToZonedTime(date, 'UTC');
  const formattedDate = format(utcDate, 'EEE dd MMM yyyy');
  const formattedTime = format(utcDate, "hh:mm a 'UTC'");

  const className = classNames('quote-date-and-time-container flex align-center justify-center', {
    'less-than-five-days-date-container': isLessThanFiveDays,
    'more-than-five-days-date-container': !isLessThanFiveDays,
    'ppc-quote-expired-content': hasExpired,
  });

  const tooltipContent = (
    <div className="tooltip-content-quote-date-and-time flex justify-center">
      <div className="tooltip-quote-date">{formattedDate}</div>
      <div className="tooltip-quote-time">{formattedTime}</div>
    </div>
  );

  return (
    <Tooltip content={tooltipContent} component={QuoteInlineDialog}>
      <div className={className} data-testid="quote-date-and-time">
        {result}
        <EmojiFrequentIcon label="frequent-clock" />
      </div>
    </Tooltip>
  );
};
